/**
 * Sidebar Navigation
 * Left-hand navigation panel in standalone mode (BaseLayout).
 * Shows Home, E-Invoicing Identifiers, and Invoices links.
 * Auto-hides on screens below md breakpoint.
 * Hidden in embedded/iframe mode.
 */

import BusinessIcon from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { appRoutes } from "@constants/routing.constants";

const SidebarRoot = styled("nav")(({ theme }) => ({
  width: 240,
  minWidth: 240,
  borderRight: `1px solid ${grey[200]}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  [theme.breakpoints.down("md")]: {
    display: "none"
  }
}));

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("base");

  const navItems = [
    {
      label: t("eInvoicingApp.nav.home"),
      path: appRoutes.home,
      icon: <HomeIcon />
    },
    {
      label: t("eInvoicingApp.nav.eInvoicingIdentifiers"),
      path: appRoutes.eInvoicingIdentifiers,
      icon: <BusinessIcon />
    },
    {
      label: t("eInvoicingApp.nav.invoices"),
      path: appRoutes.invoices,
      icon: <ReceiptIcon />
    }
  ];

  const isActive = (path: string) =>
    path === appRoutes.eInvoicingIdentifiers
      ? location.pathname.startsWith(path)
      : location.pathname === path;

  return (
    <SidebarRoot aria-label={t("eInvoicingApp.nav.home")}>
      <List sx={{ flex: 1, pt: 2 }} role="menubar">
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            role="menuitem"
            selected={isActive(item.path)}
            onClick={() => navigate(item.path)}
            aria-current={isActive(item.path) ? "page" : undefined}
            sx={{
              mx: 1,
              borderRadius: "8px",
              mb: 0.5,
              "&.Mui-selected": {
                backgroundColor: grey[100],
                "&:hover": { backgroundColor: grey[200] }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{ primary: { variant: "bodyText" } }}
            />
          </ListItemButton>
        ))}
      </List>
    </SidebarRoot>
  );
};

export default Sidebar;
